import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, HelpCircle, Mail } from "lucide-react";
import { FAQ_GROUPS } from "@/content/legal";
import SEO from "@/components/SEO";
import AdSlot from "@/components/AdSlot";
import CachePublicShell from "@/components/CachePublicShell";

export default function Faq() {
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const groups = q
    ? FAQ_GROUPS.map((g) => ({
        ...g,
        items: g.items.filter((it) =>
          it.q.toLowerCase().includes(q) || it.a.toLowerCase().includes(q)
        ),
      })).filter((g) => g.items.length > 0)
    : FAQ_GROUPS;

  // FAQPage JSON-LD: limit to the first ~20 questions so the payload
  // stays reasonable.
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ_GROUPS.flatMap((g) => g.items).slice(0, 20).map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: { "@type": "Answer", text: it.a },
    })),
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14"
         data-testid="faq-page">
      <CachePublicShell />
      <SEO
        title="Frequently Asked Questions: ConfirmPH"
        description="Quick answers about booking confirmation, rental verification, business plans, and how ConfirmPH works for renters and businesses in the Philippines."
        canonical="/faq"
        jsonLd={[faqJsonLd]}
      />
      <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">
        ConfirmPH · Help
      </p>
      <h1 className="font-display text-3xl sm:text-4xl font-bold mt-2 inline-flex items-center gap-2">
        <HelpCircle className="w-7 h-7 text-emerald-600" />
        Frequently Asked Questions
      </h1>
      <p className="text-slate-600 mt-2">
        Quick answers for customers, businesses, and billing questions.
      </p>

      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search FAQ…"
        data-testid="faq-search"
        className="mt-6 w-full rounded-lg border border-slate-300 px-4 py-2.5 text-sm focus:border-[#1B4332] focus:outline-none"
      />

      <div className="mt-8 space-y-10">
        {groups.length === 0 && (
          <p className="text-sm text-slate-500" data-testid="faq-empty">
            No FAQ items match your search.
          </p>
        )}
        {groups.map((g) => (
          <section key={g.title} data-testid={`faq-group-${g.title.toLowerCase().replace(/\s+/g, "-")}`}>
            <h2 className="font-display text-xl font-semibold">{g.title}</h2>
            <div className="mt-3 divide-y divide-slate-200 border border-slate-200 rounded-xl overflow-hidden bg-white">
              {g.items.map((it, i) => (
                <FaqItem key={i} q={it.q} a={it.a} />
              ))}
            </div>
          </section>
        ))}
      </div>

      <div className="mt-12 rounded-xl border border-slate-200 bg-slate-50 p-5 text-sm flex flex-wrap items-center gap-3"
           data-testid="faq-cta-support">
        <Mail className="w-4 h-4 text-slate-600" />
        Still need help?
        <Link to="/contact" className="underline font-medium" data-testid="faq-cta-contact">Contact us</Link>
        or
        <Link to="/support" className="underline font-medium" data-testid="faq-cta-support-page">visit Support</Link>.
      </div>

      <AdSlot placement="faq_bottom" className="mt-6" />
    </div>
  );
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <button
      onClick={() => setOpen((v) => !v)}
      className="w-full text-left p-4 hover:bg-slate-50 transition-colors"
      data-testid={`faq-item-${q.slice(0, 24).replace(/\s+/g, "-").toLowerCase()}`}
    >
      <div className="flex items-start justify-between gap-3">
        <span className="font-medium text-slate-900">{q}</span>
        <ChevronDown className={`w-4 h-4 mt-0.5 text-slate-500 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </div>
      {open && <p className="mt-2 text-sm text-slate-700 leading-relaxed">{a}</p>}
    </button>
  );
}
